// Added by Sejune Oh on 2019-02-16
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'
import jwtDecode from 'jwt-decode'
import axios from 'axios'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import RootStore from '../../index'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import {
  DEFAULT_LANG,
  USER_ACCESS_LEVEL,
  SIGN_ERR_STATUS_CODE,
  AUTH_TOKEN_COOKIE,
  StopServerReason,
  HTTP_HEADER_IGNORE_INTERCEPTOR,
  UNKNOWN_USER_NAME,
  COOKIE_IDLE_TIMER,
  COOKIE_LAST_ACTIVE_TIME,
  KEEP_ME_LOGGED_IN_COOKIE,
  ACCESS_TOKEN,
  HVN_INTEGRATED,
} from '@/helper/define'
import { deleteCookie, getQueryPath, getCookie, setCookie } from '@/helper/util'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import {
  reqAuthenticate,
  reqAuthenticateAccessToken,
  reqAuthenticateLinkToken,
  setTokenCookieAndHeaderAuthProp,
  reqRefresh,
  reqCreateSysAdmin,
  reqGetAuthenticatedUserInfo,
  reqTwoFactorData,
  reqPostTwoFactorAuth,
  reqPostTwoFactorRegister,
} from './UserAuth.service'
import ProductStore from '@/stores/ProductStore'
import { UserElementProps } from '../../mgmt/user'
import { DialogContentProps } from '@/components/common/Dialog'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface AuthorizedUserElementProps extends UserElementProps {
  permission?: string | null
  access_token_exp?: number
  refresh_token_exp?: number
  external?: boolean
  keep_me_logged_in?: boolean
}

export interface AuthTokenProps {
  access_token: string | null
  refresh_token: string | null
  max_login_attempts?: number
  failed_count?: number
}
export interface Auth2_data {
  url: string
  secrete: string
  code: string
}
export enum TokenValidStatusProps {
  ACCESS_TOKEN_EXPIRED,
  REFRESH_TOKEN_EXPIRED,
  VALID,
}

interface UpdateTokenExp {
  (authToken: AuthTokenProps, userInfo: AuthorizedUserElementProps): Promise<{
    authToken: AuthTokenProps
    userInfo: AuthorizedUserElementProps
  }>
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class UserAuthStore {
  root?: RootStore

  _authToken: AuthTokenProps | null = null
  _userInfo: AuthorizedUserElementProps | null = null
  _lang: string = DEFAULT_LANG
  isExpired: boolean = false
  isInvalidParam: boolean = false
  tokenRefreshTimeOut: number = 0
  loginFailedCnt: number = 0
  product: ProductStore | null = null
  _timeDiff: number = 0
  _timeDiffIntervalId?: any = 0

  constructor(root: RootStore) {
    this.root = root
    makeAutoObservable(this)
    this.product = new ProductStore(root)
    this.initTimeDiffWithServer()
    this._timeDiffIntervalId = setInterval(() => this.initTimeDiffWithServer(), 60 * 60 * 1000) // 1시간에 한 번 체크
  }

  /**
   * Server와 Client의 시간 차이
   */
  async initTimeDiffWithServer() {
    const serverTime = await this.product!.getServerTime() // 10자리 utc
    this._timeDiff = serverTime - Math.round(Date.now() / 1000)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get accessToken(): string | null {
    if (!this._authToken) return null
    return this._authToken.access_token || null
  }

  get refreshToken(): string | null {
    if (!this._authToken) return null
    return this._authToken.refresh_token || null
  }

  get isSignedUp(): boolean {
    return !!this._authToken
  }

  get isPasswordInitializationRequired(): boolean {
    if (!this._userInfo) return false
    return this._userInfo.change_password_at_next_login!
  }

  get isAdminUser(): boolean {
    if (!this._userInfo) return false
    return this._userInfo.access_level! >= USER_ACCESS_LEVEL.AccessLevelSysAdmin.enumValue
  }

  get isUsualUser(): boolean {
    if (!this._userInfo) return false
    return this._userInfo.access_level! >= USER_ACCESS_LEVEL.AccessLevelUsual.enumValue
  }

  get isExternalUser() {
    if (!this._userInfo) return false
    return this._userInfo.external
  }

  get shortenedUILang(): string {
    if (!this._userInfo) return this._lang.slice(0, 2)
    return this._userInfo!.ui_language!.slice(0, 2)
  }

  get fullUILang(): string {
    return this._lang
  }

  get userName(): string {
    if (!this._userInfo) return UNKNOWN_USER_NAME
    return this._userInfo.username!
  }

  get userId(): string | null {
    if (!this._userInfo) return null
    return this._userInfo.id!
  }

  get userInfo() {
    return this._userInfo!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _clearAuthToken = () => {
    this._authToken = null
  }

  _setAuthToken = (authToken: AuthTokenProps) => {
    this._authToken = authToken
  }

  _clearUserInfo = () => {
    this._userInfo = null
  }

  _setUserInfo = (userInfo: AuthorizedUserElementProps) => {
    this._userInfo = userInfo
    this._lang = this.userInfo.ui_language!
    sessionStorage.setItem('userid', userInfo.id || '')
  }

  _initializeAuthenticatedInfo = (authToken: AuthTokenProps) => {
    try {
      if (authToken.failed_count! > 0) this.loginFailedCnt = authToken.max_login_attempts! - authToken.failed_count!
      else this.loginFailedCnt = 0

      const userInfo = this.decodeUserInfo(authToken.access_token!)
      if (!userInfo) return SIGN_ERR_STATUS_CODE['400']

      // Set axios heaer props
      setTokenCookieAndHeaderAuthProp(authToken)

      this._setUserInfo(userInfo!)
      this._setAuthToken(authToken)

      // Set axios heaer props
      if (this.root!.userPref) this.root!.userPref.prefSet({ ...userInfo! })
      Logger.devLog(
        `[UserAuthStore] > Token is valid for ${(userInfo.access_token_exp! - Date.now() / 1000) / 60} min`
      )

      // 5분 후에 expired될 수 있으므로 4분 30초 간격으로 업데이트한다.
      // 이 값을 수정할 경우, dng-golang/src/backend/server/auth/service/auth.go 파일의 시간도 변경해야 함.
      this.tokenRefreshTimeOut = 5 * 60 - 30

      return null
    } catch (e) {
      Logger.errLog('[UserAuthStore] > Error occured from initializeAuthenticatedInfo')
      console.log(e)
      return e.message
    }
  }

  clearPasswordInitialization = () => {
    this._userInfo!.change_password_at_next_login = false
  }

  changeLang = (lang: string) => {
    this._lang = lang
  }

  updateUserInfo = (userInfo: UserElementProps) => {
    this._userInfo = {
      ...this._userInfo,
      ...userInfo,
    }
    this._lang = this.userInfo.ui_language!
  }

  signOutBeforeStartAppServer = () => {
    deleteCookie(AUTH_TOKEN_COOKIE)
    deleteCookie(ACCESS_TOKEN)
    // To integrate with the havana project
    if (localStorage.getItem(HVN_INTEGRATED)) {
      window.top?.postMessage('aview.sign_out', '*')
    } else {
      this._clearAuthToken()
      this._clearUserInfo()
    }
    if (this.root!.userPref) this.root!.userPref.prefClear()
  }

  clearExpiredFlag = () => {
    this.isExpired = false
  }

  clearInvalidParamFlag = () => {
    this.isInvalidParam = false
  }

  refreshAccessToken = async ({
    authTokenCookie,
    force = false,
  }: {
    authTokenCookie: string | null
    force?: boolean
  }) => {
    try {
      // AccessToken이 존재하지 않음
      if (!authTokenCookie || authTokenCookie.toLocaleLowerCase() === 'null') {
        throw new Error('[UserAuthStore] > Fail to refresh access Token (Empty token)')
      }

      const authToken = JSON.parse(authTokenCookie!) as AuthTokenProps
      const decodedUserInfo = authToken && authToken.access_token ? this.decodeUserInfo(authToken.access_token!) : null

      // Decoded 된 UserInfo가 존재하지 않음.
      if (!decodedUserInfo) {
        throw new Error('[UserAuthStore] > Empty userinfo')
      }

      this._checkAccessTokenValid(decodedUserInfo)

      // Access Token 갱신
      const {
        authToken: { access_token, refresh_token },
      } = await this[force ? '_updateTokenExpForecely' : '_updateTokenExp'](authToken, decodedUserInfo)

      // KeepMeLoggedIn을 사용 중이었다면 쿠키를 새로 셋팅함.
      const keepMeLoggedInFlag = getCookie(KEEP_ME_LOGGED_IN_COOKIE)
      const isKeepMeLoggedInChecked = keepMeLoggedInFlag === 'true'
      if (isKeepMeLoggedInChecked || decodedUserInfo.keep_me_logged_in) {
        setCookie({
          name: KEEP_ME_LOGGED_IN_COOKIE,
          value: 'true',
          expHours: 24 * 365,
        })
      } else {
        deleteCookie(KEEP_ME_LOGGED_IN_COOKIE)
      }

      // KeepMeLoggedIn을 사용하지 않는 경우
      if (!this.root!.product!.enableKeepUserLoggedIn) {
        deleteCookie(KEEP_ME_LOGGED_IN_COOKIE)
      }

      // 새로이 갱신된 AccessToken
      const eMessage = this._initializeAuthenticatedInfo({
        access_token: access_token!,
        refresh_token: refresh_token!,
      })
      if (eMessage) return Promise.reject(eMessage)
      return Promise.resolve(true)
    } catch (err) {
      Logger.devLog(`[UserAuthStore] > ${err}`)
      await this.signOut()
      return Promise.resolve(true)
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Public
  decodeUserInfo = (accessToken: string | null): AuthorizedUserElementProps | null => {
    if (!accessToken) {
      Logger.errLog('[UserAuthStore] > AccessToken is null')
      return null
    }

    const userInfo = this._decodeAccessToken(accessToken)
    if (!userInfo) {
      Logger.errLog('[UserAuthStore] > Invalid access token')
      return null
    }

    return {
      ...userInfo,
      ui_language: userInfo!.ui_language || DEFAULT_LANG,
    }
  }

  checkTokenValidStatus = ({
    access_token_exp,
    refresh_token_exp,
  }: {
    access_token_exp: number
    refresh_token_exp: number
  }) => {
    /**
     * Server에서 보내주는 시간 차를 비교하여 expired를 판단한다
     */
    const calibratedTime = Math.round(Date.now() / 1000) + this._timeDiff
    // Refresh token is expired
    if (calibratedTime > refresh_token_exp) return TokenValidStatusProps.REFRESH_TOKEN_EXPIRED

    // Access token is expires -> Refresh tokens
    if (calibratedTime > access_token_exp) return TokenValidStatusProps.ACCESS_TOKEN_EXPIRED

    return TokenValidStatusProps.VALID
  }

  signIn = async (authToken: AuthTokenProps, isConfigRequired = true) => {
    try {
      const eMessage = this._initializeAuthenticatedInfo(authToken)
      if (eMessage) return Promise.reject(eMessage)

      // Load site & package config
      if (isConfigRequired) {
        const getConfigPath = getQueryPath({})

        await Promise.all([this.root!.siteConfig!.initialize(), this.root!.packageConfig!.getElements(getConfigPath)])
      }

      return Promise.resolve(true)
    } catch (err) {
      const errStatus = await this._handleSignInErr(err)
      if (errStatus) return Promise.reject(errStatus)
      return Promise.resolve(true)
    }
  }

  checkUserInfo = async ({ email = '', password = '', keep_me_logged_in = false }) => {
    try {
      const { data: authToken } = await reqAuthenticate({ auth_id: email, password, keep_me_logged_in })

      if (authToken.failed_count! > 0) this.loginFailedCnt = authToken.max_login_attempts! - authToken.failed_count!
      else this.loginFailedCnt = 0

      const userInfo = this.decodeUserInfo(authToken.access_token!)
      if (!userInfo) return Promise.reject(false)

      return Promise.resolve({ authToken, userInfo })
    } catch (err) {
      const errStatus = await this._handleSignInErr(err)
      if (errStatus) return Promise.reject(errStatus)
      return Promise.reject(false)
    }
  }

  // register 2FA
  twoFactorRegister = async (auth2: Readonly<{}> | undefined, id: string) => {
    try {
      await reqPostTwoFactorRegister(id, JSON.stringify(auth2))
      return true
    } catch (err:any) {
      if (err.response.status === 401) {
        return false
      }
      return Promise.resolve(false)
    }
  }

  // auth 2FA
  twoFactorAuth = async (auth2: Readonly<{}> | undefined, id: string) => {
    try {
      await reqPostTwoFactorAuth(id, JSON.stringify(auth2))
      return true
    } catch (err:any) {
      if (err.response.status === 401) {
        return false
      }
      return Promise.resolve(false)
    }
  }

  getTwoFactorData = async (id: string) => {
    try {
      const { data } = await reqTwoFactorData(id)
      const auth2_data: { url: string; secret: string; code: string } = JSON.parse(data.auth2_data)
      return auth2_data
    } catch (err) {
      const errStatus = await this._handleSignInErr(err)
      if (errStatus) return Promise.reject(errStatus)
      return Promise.resolve(true)
    }
  }

  

  signInWithAccessToken = async (accessToken: string) => {
    try {
      const { data: authToken } = await reqAuthenticateAccessToken({ access_token: accessToken })
      const eMessage = this._initializeAuthenticatedInfo(authToken)
      if (eMessage) return Promise.reject(eMessage)
      return Promise.resolve(true)
    } catch (err) {
      const errStatus = await this._handleSignInErr(err)
      if (errStatus) return Promise.reject(errStatus)
      return Promise.resolve(true)
    }
  }

  signInWithLinkToken = async (linkToken: string) => {
    try {
      const { data: authToken } = await reqAuthenticateLinkToken({ link_token: linkToken })

      if (Object.keys(authToken).length === 0) {
        Logger.devLog('[UserAuthStore] > Fail to authenticate with link token (Empty)')
        await this.signOut()
        return Promise.resolve(true)
      }

      const eMessage = this._initializeAuthenticatedInfo({
        access_token: authToken.access_token!,
        refresh_token: authToken.refresh_token!,
      })
      if (eMessage) return Promise.reject(eMessage)
      return Promise.resolve(true)
    } catch (err) {
      const errStatus = await this._handleSignInErr(err)
      if (errStatus) return Promise.reject(errStatus)
      return Promise.resolve(true)
    }
  }

  signOut = async (clearHistory = false, reason?: DialogContentProps, onlyMySession?: boolean) => {
    if (this.root!.clApp!.isPromptRequired) {
      const answer = window.confirm(this.root!.clApp!.promptMessage)
      if (answer) {
        this.root!.clApp!.clearMessageBeforeWebPageUnload()
        await this._signOut(clearHistory, onlyMySession)
      } else {
        return false
      }
    } else {
      await this._signOut(clearHistory, onlyMySession)
    }

    if (reason) {
      this.root!.globalEvt!.openDialog(
        {
          ...reason,
          hideCancelBtn: true,
          disableBackdropClick: false,
        },
        () => {
          return new Promise((resolve) => resolve(true))
        }
      )
    }

    return true
  }

  /** Viewer 쪽에서도 나타날 수 있는 메시지라면 LoginFormContainer에도 추가 */
  getSignInErrMessage(Intl: any, errMsg: string) {
    switch (errMsg) {
      case 'denied':
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_access_denied' })
      case 'wPwinfo':
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_wrong_password' })

      case 'winfo':
        if (this.loginFailedCnt > 0)
          return Intl.getIntlTextWithOption({ id: 'htmljs_react.global.userauth.msg_wrong_info_with_option' },{ remaining: this.loginFailedCnt})
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_wrong_info' })
      case 'wTwoFactorCode':
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_wrong_two_factor_code' })
      case 'deactivated':
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_deactivated' })
      case 'idNotEmpty':
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_id_empty' })
      case 'pwNotEmpty':
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_pw_empty' })
      case 'expired':
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_expired' })
      default:
        return Intl.getIntlText({ id: 'htmljs_react.global.userauth.msg_network_error' })
    }
  }

  signUpSystemAdminUser = ({
    username,
    display_name,
    email,
    password,
    description,
    ui_language,
  }: UserElementProps) => {
    return reqCreateSysAdmin({
      username: username!,
      display_name: display_name!,
      email: email!,
      password: password!,
      description: description!,
      ui_language: ui_language!,
    })
  }

  authenticate = async (id: string, password: string) => {
    try {
      const { status } = await reqAuthenticate({ auth_id: id, password, check_only: true })
      return Promise.resolve(status === 200)
    } catch (err) {
      Logger.errLog('[UserAuthStore] > Error from authenticating user')
      console.log(err)
      const eMessage = err.response ? SIGN_ERR_STATUS_CODE[`${err.response.status}`] : err.message
      return Promise.reject(eMessage)
    }
  }

  getAuthenticatedUserInfo = async (username: string) => {
    return await reqGetAuthenticatedUserInfo({ username })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Private
  _decodeAccessToken = (accessToken: string): AuthorizedUserElementProps | null => {
    try {
      const { exp, payload } = jwtDecode<any>(accessToken)
      if (!payload) return null

      return { ...(payload as AuthorizedUserElementProps), access_token_exp: exp }
    } catch (err) {
      Logger.errLog(`[UserAuthStore] > Can not decode access token : ${accessToken}`)
      console.log(err)
      return null
    }
  }

  _updateTokenExp: UpdateTokenExp = async (authToken, userInfo) => {
    const { access_token_exp, refresh_token_exp } = userInfo

    const tokenStatus = this.checkTokenValidStatus({
      access_token_exp: access_token_exp!,
      refresh_token_exp: refresh_token_exp!,
    })

    switch (tokenStatus) {
      case TokenValidStatusProps.REFRESH_TOKEN_EXPIRED: {
        Logger.devLog('[UserAuthStore] > Tokens are expired.')
        await this.signOut()
        return Promise.reject()
      }
      case TokenValidStatusProps.ACCESS_TOKEN_EXPIRED: {
        Logger.devLog('[UserAuthStore] > Refresh access token with refresh token.')
        const updatedAuthToken = await this._refreshAccessTokenWithFlag(authToken)
        const updatedUserInfo = this.decodeUserInfo(updatedAuthToken.access_token!)!
        return Promise.resolve({
          authToken: updatedAuthToken,
          userInfo: updatedUserInfo,
        })
      }
      default:
        return Promise.resolve({
          authToken,
          userInfo,
        })
    }
  }

  _updateTokenExpForecely: UpdateTokenExp = async (authToken) => {
    const refreshedAuthToken = await this._refreshAccessTokenWithFlag(authToken)
    const updatedUserInfo = this.decodeUserInfo(refreshedAuthToken.access_token!)!

    return Promise.resolve({
      authToken: refreshedAuthToken,
      userInfo: updatedUserInfo,
    })
  }

  _refreshAccessTokenWithFlag = async (authToken: AuthTokenProps) => {
    try {
      // Set flag for axios interceptor
      axios.defaults.headers.common[HTTP_HEADER_IGNORE_INTERCEPTOR] = 'true'

      const { data: refreshedAuthToken } = await reqRefresh(authToken)
      Logger.devLog('[UserAuthStore] > Auth token is refreshed')
      return Promise.resolve(refreshedAuthToken)
    } catch (err) {
      Logger.devLog('[UserAuthStore] > Refreshing auth token has been failed.')
      await this.signOut()
      return Promise.reject(err)
    } finally {
      // # Clear flag
      axios.defaults.headers.common[HTTP_HEADER_IGNORE_INTERCEPTOR] = ''
    }
  }

  _handleSignInErr = async (err: any) => {
    if (!err.response) {
      Logger.errLog('[UserAuthStore] > Error from UserAuth.signIn\nLost Server Connection.')
      return SIGN_ERR_STATUS_CODE['500']
    }

    const {
      response: {
        data: { error },
        status,
      },
    } = err

    if (error === 'expired') {
      Logger.errLog('[UserAuthStore] > Error from UserAuth.signInWithLinkToken\nToken is expired.')
      await this.signOut()
      this.isExpired = true
      return null
    }

    if (status === 404) {
      Logger.errLog('[UserAuthStore] > Error from UserAuth.signInWithLinkToken')
      await this.signOut()
      this.isInvalidParam = true
      return null
    }

    Logger.errLog('[UserAuthStore] > Error from UserAuth.signIn')
    return SIGN_ERR_STATUS_CODE[`${status}`]
  }

  _signOut = (clearHistory: boolean, onlyMySession?: boolean) => {
    try {
      if (this.isSignedUp) {
        this.root!.clApp!.stopAppServer(true, StopServerReason.USER_LOGOUT, this.userInfo.username, onlyMySession)
      }
      deleteCookie(COOKIE_IDLE_TIMER)
      deleteCookie(COOKIE_LAST_ACTIVE_TIME)
      deleteCookie(AUTH_TOKEN_COOKIE)
      deleteCookie(ACCESS_TOKEN)
    } catch (err) {
      Logger.errLog('[UserAuthStore] > Error occured from signout')
      console.log(err)
    } finally {
      clearInterval(this._timeDiffIntervalId)

      // To integrate with the havana project
      if (localStorage.getItem(HVN_INTEGRATED)) {
        window.top?.postMessage('aview.sign_out', '*')
      } else {
        this._clearAuthToken()
        this._clearUserInfo()
      }

      if (this.root!.userPref) this.root!.userPref.prefClear()
      if (clearHistory) this.root!.historyCtr!.clearHistory()
      this.root!.clApp!.viewerLogout()
      Promise.resolve(true)
    }
  }

  _checkAccessTokenValid = (decodedUserInfo: AuthorizedUserElementProps) => {
    // Valid check
    const { access_token_exp, refresh_token_exp, keep_me_logged_in } = decodedUserInfo
    const tokenStatus = this.checkTokenValidStatus({
      access_token_exp: access_token_exp!,
      refresh_token_exp: refresh_token_exp!,
    })

    switch (tokenStatus) {
      case TokenValidStatusProps.ACCESS_TOKEN_EXPIRED:
        if (!keep_me_logged_in) {
          throw new Error('[UserAuthStore] > Access token is already expired.')
        }
        break
      case TokenValidStatusProps.REFRESH_TOKEN_EXPIRED:
        throw new Error('[UserAuthStore] > Refresh Token is already expired.')
    }

    return null
  }
}
